import { ListItemSpacing, ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import {
  MessageActionMenuItem,
  MessageViewStandardAvatarPosition,
  MessageViewVariant
} from '../../../types/enums';

import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import type { TkbMessageWithRepliesWidgetProps } from '../../messages/types';
import { ContributorsListType } from '../../messages/types';
import useTranslation from '../../useTranslation';
import localStyles from './TkbArticleWidget.module.pcss';
import KbArticleGuideNavigation from './KbArticleGuideNavigation/KbArticleGuideNavigation';
import useGuideNavigationForArticle, { NavigationType } from './useGuideNavigationForArticle';
import TkbArticle from './TkbArticle/TkbArticle';

/**
 * TKB Article Widget.
 * @constructor
 * @author Sravan Reddy
 */
const TkbArticleWidget: WidgetFC<TkbMessageWithRepliesWidgetProps> = ({
  editLevel,
  instanceId,
  className,
  listVariant = {
    type: ListVariant.UNSTYLED,
    props: {
      listItemSpacing: ListItemSpacing.SM
    }
  },
  viewVariant = {
    type: MessageViewVariant.STANDARD,
    props: {
      avatarPosition: MessageViewStandardAvatarPosition.TOP,
      useReplies: true,
      useRepliesCount: true,
      useNodeLink: false,
      useKudosCount: true,
      useAcceptedSolutionButton: false,
      useTags: true,
      useFooterDivider: true,
      useSolvedBadge: false,
      useIntroduction: true,
      useAttachments: true,
      adminActionMenuItems: [
        { item: MessageActionMenuItem.EDIT_INLINE },
        { item: MessageActionMenuItem.MOVE_COMMENT },
        { item: MessageActionMenuItem.ARTICLE_HISTORY },
        { item: MessageActionMenuItem.COPY_LINK },
        { item: MessageActionMenuItem.SUBSCRIBE },
        { item: MessageActionMenuItem.DELETE }
      ],
      actionMenuItems: [
        { item: MessageActionMenuItem.REPORT_ABUSE },
        { item: MessageActionMenuItem.MARK_AS_APPROVED },
        { item: MessageActionMenuItem.REJECT },
        { item: MessageActionMenuItem.MARK_AS_NOT_SPAM },
        { item: MessageActionMenuItem.BAN_MEMBER },
        { item: MessageActionMenuItem.EDIT_BAN_MEMBER }
      ],
      className: 'lia-g-message-box',
      useAccordionForReply: true,
      flattenDepth: 7,
      useReadOnlyIcon: true,
      useCustomFields: true
    }
  },
  pagerVariant,
  pageSize = 10,
  lazyFetchReplies = true,
  showGuideNavigationSection = true,
  showTimestamp = true,
  showVersion = true,
  showHelpfulness = true,
  contributorListType = ContributorsListType.LINK
}) => {
  const cx = useClassNameMapper(localStyles);
  const { loading: textLoading } = useTranslation(EndUserComponent.TKB_ARTICLE_WIDGET);

  const { navigationType, guideContentArticle, isGuidesAndChaptersEnabled } =
    useGuideNavigationForArticle(module);

  if (textLoading) {
    return null;
  }

  const showNavigationPanel =
    showGuideNavigationSection &&
    isGuidesAndChaptersEnabled &&
    navigationType !== NavigationType.NONE;

  return (
    <EditableWidget
      props={{
        editLevel,
        instanceId,
        showGuideNavigationSection,
        showTimestamp,
        showVersion,
        showHelpfulness,
        contributorListType
      }}
    >
      <div className={cx({ 'lia-article-scroll-wrap': showNavigationPanel })}>
        {showNavigationPanel && (
          <KbArticleGuideNavigation
            className={cx('lia-article-scroll-nav')}
            navigationType={navigationType}
            guideContentArticle={guideContentArticle}
          />
        )}
        <div className={cx(className, { 'lia-article-scroll-content': showNavigationPanel })}>
          <TkbArticle
            viewVariant={viewVariant}
            listVariant={listVariant}
            showNavigationPanel={showNavigationPanel}
            pagerVariant={pagerVariant}
            pageSize={pageSize}
            lazyFetchReplies={lazyFetchReplies}
            showHelpfulness={showHelpfulness}
            showTimestamp={showTimestamp}
            showVersion={showVersion}
            contributorListType={contributorListType}
          />
        </div>
      </div>
    </EditableWidget>
  );
};

export default TkbArticleWidget;
